// apps/web/src/components/Sidebar.js
import React, { useState } from 'react';
import './Sidebar.css';
import UpgradeModal from './UpgradeModal';
import SubscriptionBadge from './SubscriptionBadge';

export default function Sidebar({
  workspaces = [],
  selectedWorkspaceId,
  onSelectWorkspace,
  newWorkspaceName,
  setNewWorkspaceName,
  onCreateWorkspace,

  lists = [],
  selectedListId,
  onSelectList,
  newListName,
  setNewListName,
  onCreateList,

  // NEW: delete handlers
  onDeleteWorkspace,
  onDeleteList,

  // NEW: rename handlers
  onRenameWorkspace,
  onRenameList,

  isAuthed,

  // NEW: subscription & limits
  user,
  limits,
}) {
  const [editingWorkspaceId, setEditingWorkspaceId] = useState(null);
  const [editingListId, setEditingListId] = useState(null);
  const [editWorkspaceName, setEditWorkspaceName] = useState('');
  const [editListName, setEditListName] = useState('');

  // Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeContext, setUpgradeContext] = useState({});

  const handleStartEditWorkspace = (workspace, e) => {
    e.stopPropagation();
    setEditingWorkspaceId(workspace.id);
    setEditWorkspaceName(workspace.name);
  };

  const handleSaveWorkspaceName = (workspaceId, e) => {
    e.stopPropagation();
    if (editWorkspaceName.trim()) {
      onRenameWorkspace(workspaceId, editWorkspaceName.trim());
    }
    setEditingWorkspaceId(null);
    setEditWorkspaceName('');
  };

  const handleCancelEditWorkspace = (e) => {
    e.stopPropagation();
    setEditingWorkspaceId(null);
    setEditWorkspaceName('');
  };

  const handleStartEditList = (list, e) => {
    e.stopPropagation();
    setEditingListId(list.id);
    setEditListName(list.title);
  };

  const handleSaveListName = (listId, e) => {
    e.stopPropagation();
    if (editListName.trim()) {
      onRenameList(listId, editListName.trim());
    }
    setEditingListId(null);
    setEditListName('');
  };

  const handleCancelEditList = (e) => {
    e.stopPropagation();
    setEditingListId(null);
    setEditListName('');
  };

  // Handle workspace creation with limit check
  const handleCreateWorkspace = () => {
    // Check if user is at workspace limit (free tier)
    if (user && limits && user.subscription_tier === 'free') {
      const atLimit = limits.workspaces.current >= limits.workspaces.max;

      if (atLimit) {
        setUpgradeContext({
          feature: 'Unlimited Workspaces',
          currentLimit: `${limits.workspaces.current}/${limits.workspaces.max} workspace`,
          benefits: [
            'Create unlimited workspaces',
            'Create unlimited lists',
            'Invite unlimited collaborators',
            'Access premium templates',
            'Priority support'
          ]
        });
        setShowUpgradeModal(true);
        return; // Don't create workspace
      }
    }

    // User is within limits or is paid, proceed
    onCreateWorkspace();
  };

  // Handle list creation with limit check
  const handleCreateList = () => {
    // Check if user is at list limit for this workspace (free tier)
    if (user && limits && user.subscription_tier === 'free') {
      const listsInWorkspace = lists.length;
      const atLimit = listsInWorkspace >= limits.lists_per_workspace;

      if (atLimit) {
        setUpgradeContext({
          feature: 'Unlimited Lists',
          currentLimit: `${listsInWorkspace}/${limits.lists_per_workspace} list`,
          benefits: [
            'Create unlimited lists per workspace',
            'Organise projects your way',
            'Invite collaborators to lists',
            'Access premium templates'
          ]
        });
        setShowUpgradeModal(true);
        return; // Don't create list
      }
    }

    // User is within limits or is paid, proceed
    onCreateList();
  };

  return (
    <aside className="Sidebar" role="navigation" aria-label="Workspace and list navigation">
      {/* User info section with subscription badge */}
      {isAuthed && user && (
        <div className="SidebarUserInfo">
          <div className="SidebarUserEmail">{user.email}</div>
          <div className="SidebarUserRole">Role: {user.role || 'user'}</div>
          <SubscriptionBadge tier={user.subscription_tier} size="small" />
        </div>
      )}

      <div className="SidebarSection">
        <div className="SidebarSectionTitle">Workspaces</div>

        {!isAuthed ? (
          <div className="SidebarInfo">Log in to see your workspaces.</div>
        ) : (
          <>
            <ul className="SidebarList">
              {workspaces.map((w) => (
                <li
                  key={w.id}
                  className={'SidebarItem ' + (w.id === selectedWorkspaceId ? 'active' : '')}
                  onClick={() => editingWorkspaceId !== w.id && onSelectWorkspace(w.id)}
                  title={w.name}
                  role="button"
                  aria-current={w.id === selectedWorkspaceId ? 'true' : undefined}
                >
                  {editingWorkspaceId === w.id ? (
                    <div className="SidebarItemEdit" onClick={(e) => e.stopPropagation()}>
                      <input
                        className="SidebarInput"
                        value={editWorkspaceName}
                        onChange={(e) => setEditWorkspaceName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveWorkspaceName(w.id, e);
                          if (e.key === 'Escape') handleCancelEditWorkspace(e);
                        }}
                        autoFocus
                        aria-label="Workspace name"
                      />
                      <button
                        className="Btn small"
                        onClick={(e) => handleSaveWorkspaceName(w.id, e)}
                        title="Save"
                        aria-label="Save workspace name"
                      >
                        ✓
                      </button>
                      <button
                        className="Btn small"
                        onClick={handleCancelEditWorkspace}
                        title="Cancel"
                        aria-label="Cancel editing workspace name"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className="SidebarItemLabel">
                        {w.name || 'Untitled workspace'}
                      </span>
                      <div className="SidebarItemActions">
                        <button
                          className="SidebarItemEditBtn"
                          onClick={(e) => handleStartEditWorkspace(w, e)}
                          title="Rename workspace"
                          aria-label={`Rename workspace ${w.name || 'Untitled workspace'}`}
                        >
                          ✎
                        </button>
                        <button
                          className="SidebarItemDeleteBtn"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete workspace "${w.name || 'Untitled workspace'}" and all its lists?`)) {
                              onDeleteWorkspace(w.id);
                            }
                          }}
                          title="Delete workspace"
                          aria-label={`Delete workspace ${w.name || 'Untitled workspace'}`}
                        >
                          ✕
                        </button>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>

            {/* Show workspace limit indicator for free users */}
            {user && limits && user.subscription_tier === 'free' && (
              <div className="SidebarLimitIndicator">
                {limits.workspaces.current}/{limits.workspaces.max} workspace used
              </div>
            )}

            <div className="SidebarCreateRow">
              <input
                className="SidebarInput"
                placeholder="New workspace name"
                value={newWorkspaceName}
                onChange={(e) => setNewWorkspaceName(e.target.value)}
                aria-label="New workspace name"
              />
              <button className="Btn small" onClick={handleCreateWorkspace} aria-label="Create new workspace">
                Add
              </button>
            </div>
          </>
        )}
      </div>

      <div className="SidebarSection">
        <div className="SidebarSectionTitle">Lists</div>

        {!selectedWorkspaceId ? (
          <div className="SidebarInfo">Select a workspace to see its lists.</div>
        ) : (
          <>
            <ul className="SidebarList">
              {lists.map((l) => (
                <li
                  key={l.id}
                  className={'SidebarItem ' + (l.id === selectedListId ? 'active' : '')}
                  onClick={() => editingListId !== l.id && onSelectList(l.id)}
                  title={l.title}
                  role="button"
                  aria-current={l.id === selectedListId ? 'true' : undefined}
                >
                  {editingListId === l.id ? (
                    <div className="SidebarItemEdit" onClick={(e) => e.stopPropagation()}>
                      <input
                        className="SidebarInput"
                        value={editListName}
                        onChange={(e) => setEditListName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveListName(l.id, e);
                          if (e.key === 'Escape') handleCancelEditList(e);
                        }}
                        autoFocus
                        aria-label="List name"
                      />
                      <button
                        className="Btn small"
                        onClick={(e) => handleSaveListName(l.id, e)}
                        title="Save"
                        aria-label="Save list name"
                      >
                        ✓
                      </button>
                      <button
                        className="Btn small"
                        onClick={handleCancelEditList}
                        title="Cancel"
                        aria-label="Cancel editing list name"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className="SidebarItemLabel">
                        {l.title || 'Untitled list'}
                      </span>
                      <div className="SidebarItemActions">
                        <button
                          className="SidebarItemEditBtn"
                          onClick={(e) => handleStartEditList(l, e)}
                          title="Rename list"
                          aria-label={`Rename list ${l.title || 'Untitled list'}`}
                        >
                          ✎
                        </button>
                        <button
                          className="SidebarItemDeleteBtn"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete list "${l.title || 'Untitled list'}" and all its tasks?`)) {
                              onDeleteList(l.id);
                            }
                          }}
                          title="Delete list"
                          aria-label={`Delete list ${l.title || 'Untitled list'}`}
                        >
                          ✕
                        </button>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>

            {/* Show list limit indicator for free users */}
            {user && limits && user.subscription_tier === 'free' && (
              <div className="SidebarLimitIndicator">
                {lists.length}/{limits.lists_per_workspace} list used
              </div>
            )}

            <div className="SidebarCreateRow">
              <input
                className="SidebarInput"
                placeholder="New list name"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                aria-label="New list name"
              />
              <button className="Btn small" onClick={handleCreateList} disabled={!isAuthed} aria-label="Create new list">
                Add
              </button>
            </div>
          </>
        )}
      </div>

      {/* Upgrade Modal */}
      <UpgradeModal
        show={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature={upgradeContext.feature}
        benefits={upgradeContext.benefits}
        currentLimit={upgradeContext.currentLimit}
      />
    </aside>
  );
}
